<template>
  <div class="container grid items-center gap-6 px-4 text-center md:px-6">
    <div class="space-y-3">
      <h2 class="text-3xl font-bold tracking-tighter md:text-4xl/tight">
        Get early access to Peeple
      </h2>
      <p class="mx-auto max-w-[600px] text-gray-500 md:text-xl/relaxed">
        We're launching soon. Sign up now for early access and help shape the future of authentic social
        networking.
      </p>
    </div>
    <div class="mx-auto w-full max-w-sm space-y-2">
      <form @submit.prevent="submitForm" class="space-y-4">
        <div class="space-y-2">
          <label for="email"
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Email</label>
          <input v-model="email" type="email" id="email" placeholder="your@email.com" required
            class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
          <p v-if="emailError" class="text-sm text-red-500">{{ emailError }}</p>
        </div>
        <button type="submit" :disabled="isSubmitting"
          class="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full">
          {{ isSubmitting ? "Submitting..." : "Join the Waitlist" }}
        </button>
      </form>
      <p class="text-xs text-gray-500">We'll never share your email with anyone else.</p>
    </div>

    <!-- Timeline -->
    <div class="mt-12 max-w-3xl mx-auto w-full">
      <h3 class="text-lg font-medium mb-6">What happens next?</h3>

      <!-- Desktop Timeline (horizontal) -->
      <div class="hidden md:block">
        <div class="relative">
          <!-- Timeline Line -->
          <div class="absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -translate-y-1/2"></div>

          <!-- Timeline Steps -->
          <div class="relative flex justify-between">
            <!-- Step 1 -->
            <div class="flex flex-col items-center">
              <div
                class="rounded-full bg-white border-2 border-primary-500 w-10 h-10 flex items-center justify-center z-10 mb-2">
                <span class="font-bold text-primary-500">1</span>
              </div>
              <h4 class="font-medium">Sign Up</h4>
              <p class="text-sm text-gray-500 text-center max-w-[120px] mt-1">Join our waitlist</p>
            </div>

            <!-- Step 2 -->
            <div class="flex flex-col items-center">
              <div
                class="rounded-full bg-white border-2 border-primary-500 w-10 h-10 flex items-center justify-center z-10 mb-2">
                <span class="font-bold text-primary-500">2</span>
              </div>
              <h4 class="font-medium">Receive Invite</h4>
              <p class="text-sm text-gray-500 text-center max-w-[120px] mt-1">
                Get your exclusive access code
              </p>
            </div>

            <!-- Step 3 -->
            <div class="flex flex-col items-center">
              <div
                class="rounded-full bg-white border-2 border-primary-500 w-10 h-10 flex items-center justify-center z-10 mb-2">
                <span class="font-bold text-primary-500">3</span>
              </div>
              <h4 class="font-medium">Set Up Profile</h4>
              <p class="text-sm text-gray-500 text-center max-w-[120px] mt-1">
                Connect your search history
              </p>
            </div>

            <!-- Step 4 -->
            <div class="flex flex-col items-center">
              <div
                class="rounded-full bg-white border-2 border-primary-500 w-10 h-10 flex items-center justify-center z-10 mb-2">
                <span class="font-bold text-primary-500">4</span>
              </div>
              <h4 class="font-medium">Start Sharing</h4>
              <p class="text-sm text-gray-500 text-center max-w-[120px] mt-1">
                Begin your authentic journey
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Timeline (vertical) -->
      <div class="md:hidden space-y-6">
        <!-- Step 1 -->
        <div class="flex items-start">
          <div class="flex-shrink-0 mr-4">
            <div class="rounded-full bg-white border-2 border-primary-500 w-8 h-8 flex items-center justify-center">
              <span class="font-bold text-primary-500 text-sm">1</span>
            </div>
          </div>
          <div>
            <h4 class="font-medium">Sign Up</h4>
            <p class="text-sm text-gray-500 mt-1">Join our waitlist with your email</p>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="flex items-start">
          <div class="flex-shrink-0 mr-4">
            <div class="rounded-full bg-white border-2 border-primary-500 w-8 h-8 flex items-center justify-center">
              <span class="font-bold text-primary-500 text-sm">2</span>
            </div>
          </div>
          <div>
            <h4 class="font-medium">Receive Invite</h4>
            <p class="text-sm text-gray-500 mt-1">Get your exclusive access code via email</p>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="flex items-start">
          <div class="flex-shrink-0 mr-4">
            <div class="rounded-full bg-white border-2 border-primary-500 w-8 h-8 flex items-center justify-center">
              <span class="font-bold text-primary-500 text-sm">3</span>
            </div>
          </div>
          <div>
            <h4 class="font-medium">Set Up Profile</h4>
            <p class="text-sm text-gray-500 mt-1">
              Connect your search history and customize your profile
            </p>
          </div>
        </div>

        <!-- Step 4 -->
        <div class="flex items-start">
          <div class="flex-shrink-0 mr-4">
            <div class="rounded-full bg-white border-2 border-primary-500 w-8 h-8 flex items-center justify-center">
              <span class="font-bold text-primary-500 text-sm">4</span>
            </div>
          </div>
          <div>
            <h4 class="font-medium">Start Sharing</h4>
            <p class="text-sm text-gray-500 mt-1">Begin your authentic journey on Peeple</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const showToast = ref(false)

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

const email = ref('')
const emailError = ref('')
const isSubmitting = ref(false)


const submitForm = async () => {
  // Reset error state
  emailError.value = ''

  // Validate email
  if (!email.value) {
    emailError.value = 'Email is required'
    return
  }

  if (!validateEmail(email.value)) {
    emailError.value = 'Please enter a valid email address'
    return
  }

  // Submit form
  isSubmitting.value = true

  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Show success toast
    showToast.value = true

    // Hide toast after 5 seconds
    setTimeout(() => {
      showToast.value = false
    }, 5000)

    // Reset form
    email.value = ''
  } catch (error) {
    emailError.value = 'An error occurred. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style></style>